import {
  ActivePage,
  Audience,
  AudienceSummary,
  AudienceType,
  Breadcrumb,
  BuildAudience,
  UploadAudience,
  Variable
} from "@/types";

const client = process.env.REACT_APP_CLIENT || "core";

export const defaultAudience: Audience = {
  audience_id: "",
  audience_name: "",
  client: client,
  created_at: "",
  segment: false,
  segments: [],
  audience_type: "build",
  audience_geo: "cube",
  build_mode: "balanced",
  operator: "AND",
  var_groups: [{ id: 1, variables: [], operator: "AND" }],
  export: true,
  project_name: null,
  shared: false,
};

export const defaultBuildAudience: BuildAudience = {
  audience_id: "",
  audience_name: "",
  client: client,
  created_at: "",
  segment: false,
  segments: [],
  audience_type: "build",
  audience_geo: "cube",
  build_mode: "balanced",
  operator: "AND",
  var_groups: [{ id: 1, variables: [], operator: "AND" }],
  export: true,
  project_name: null,
  shared: false,
};

export const defaultUploadAudience: UploadAudience = {
  audience_id: "",
  audience_name: "",
  client: client,
  created_at: "",
  segment: false,
  segments: [],
  audience_type: "upload",
  audience_geo: "postcode",
  upload_file: null,
  export: true,
  project_name: null,
  shared: false,
};

export const defaultVariableGroup = (id: number) => {
  return {
    id: id,
    variables: [],
    operator: "AND",
  };
};

/**
 * Build breadcrumbs for an audience page
 */
export function getAudienceBreadcrumb(
  activePage: ActivePage | null,
  audience: Audience | null,
  loading: boolean,
  page?: string
): Breadcrumb[] {
  const newAudience =
    !audience || audience.audience_id === "" || audience.audience_name === "";

  if (!activePage) return [];

  if (loading) return activePage.breadcrumbs;

  const audiencePath = `${activePage.path}/${
    newAudience ? "new" : audience.audience_id
  }`;
  const audienceTitle = newAudience ? "New" : audience.audience_name;
  const audienceBreadcrumb = { title: audienceTitle, path: audiencePath };

  if (page) {
    const pagePath = `${audiencePath}/${page}`;
    const pageBreadcrumb = { title: page, path: pagePath };

    return [...activePage.breadcrumbs, audienceBreadcrumb, pageBreadcrumb];
  } else {
    return [...activePage.breadcrumbs, audienceBreadcrumb];
  }
}

/**
 * Filter a list of audiences using search terms, multiple filters and sort
 */
export const filterAudiences = (
  audiences: Audience[] | null,
  searchText?: string,
  filterBy?: string,
  sortBy?: string
) => {
  // Filter by search text
  let filteredAudiences = audiences?.filter((audience) => {
    if (audience.segment && searchText !== "") {
      return (audience.audience_name.toLowerCase().includes(searchText?.toLowerCase() || ""))
      || (audience.segments.some(x => x.segment_name.toLowerCase().includes(searchText?.toLowerCase() || "")))
    } else {
      return audience.audience_name
        .toLowerCase()
        .includes(searchText?.toLowerCase() || "")
    }
  }

  );

  // Filter by project
  if (filterBy) {
    filteredAudiences = filteredAudiences?.filter(
      (audience) => audience.project_name === filterBy
    );
  }

  // Apply sorting
  if (sortBy) {
    if (sortBy === "name") {
      filteredAudiences?.sort((a, b) =>
        a.audience_name.localeCompare(b.audience_name)
      );
    } else if (sortBy === "date") {
      filteredAudiences?.sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() -
          new Date(a.created_at || 0).getTime()
      );
    }
  } else {
    // Make sure the audiences with no project are always shown first unless there is a sort
    filteredAudiences = [
      ...(filteredAudiences?.filter((x) => x.project_name === null) || []),
      ...(filteredAudiences?.filter((x) => x.project_name !== null) || []),
    ];
  }

  return filteredAudiences;
};

export const checkAudienceSize = (summary: AudienceSummary) => {
  if (summary.num_postcodes !== undefined && summary.num_postcodes > 0) {
    return true;
  } else return summary.num_sectors !== undefined && summary.num_sectors > 0;
};


export const checkVariables = (audience: BuildAudience, variables: Variable[]) => {
  if (variables.length === 0) return true;

  const audienceVariables = audience.var_groups.map(x => x.variables).flat();
  const variableIds = new Set(variables.map(v => v.tag_id));
  return audienceVariables.every(v => variableIds.has(v.tag_id));
}

export const checkVariable = (selection: Variable, variables: Variable[]) => {
  return variables.map(v => v.tag_id).includes(selection.tag_id);
}

export const toAudienceType = (value: string): AudienceType | undefined => {
  const audienceTypes = ['build', 'upload', 'mindset'];
  if (audienceTypes.includes(value as AudienceType)) {
    return value as AudienceType
  } else {
    return undefined
  }

}
